import React, { useCallback, useEffect, useRef } from "react";
import { useCoop } from "../contexts/CoopContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { CLASSES, ENERGY_MAX } from "../data/classes";
import { computeDamageWithFormula } from "../utils/gameLogic";
import { HealthBar } from "../components/game/StatsDisplay";
import { Swords, Zap, Crown } from "lucide-react";
import { SFX } from "../utils/audio";

// ============================================================
// CoopDuel — duel TOUR PAR TOUR entre deux joueurs coop (touche F).
// State partagé via game_state.duel (CAS). Chaque participant joue son tour.
// L'HÔTE nettoie le duel après l'affichage du résultat.
// ============================================================

// Avance le tour (mute le draft d du producer)
const advanceTurn = (d) => {
  const next = d.turnIndex + 1;
  if (next >= d.turnOrder.length) {
    d.turnIndex = 0;
    d.round = (d.round || 1) + 1;
    for (const uid of d.turnOrder) d.energy[uid] = Math.min(ENERGY_MAX, (d.energy[uid] ?? 0) + 1);
  } else {
    d.turnIndex = next;
  }
};

const CoopDuel = () => {
  const { lobby, publishState, isHost } = useCoop();
  const { user } = useAuth();
  const { unlockAchievement } = useProfile();
  const myId = user?.user_id;
  const state = lobby?.game_state;
  const rev = lobby?.state_revision;
  const duel = state?.duel;

  const publish = useCallback((producer) => {
    const cur = lobby?.game_state;
    if (!cur) return false;
    const next = producer(structuredClone(cur));
    if (!next) return false;
    return publishState(next, { expectedRevision: rev });
  }, [lobby, rev, publishState]);

  // SFX + achievements on result
  const prevPhase = useRef(duel?.phase);
  useEffect(() => {
    if (duel && duel.phase !== prevPhase.current) {
      if (duel.phase === "done") {
        (duel.winner === myId ? SFX.victory : SFX.defeat)?.();
        if (myId === duel.a || myId === duel.b) {
          unlockAchievement?.("duel_master");
          if (duel.winner === myId) unlockAchievement?.("betrayer");
        }
      }
      prevPhase.current = duel.phase;
    }
  }, [duel, myId, unlockAchievement]);

  // HOST clears the duel after showing the result (final HP/alive already applied at KO)
  const cleanupRef = useRef(null);
  useEffect(() => {
    if (!isHost || !duel || duel.phase !== "done") return;
    if (cleanupRef.current) return;
    cleanupRef.current = setTimeout(() => {
      cleanupRef.current = null;
      publish((s) => {
        const d = s.duel;
        if (!d || d.phase !== "done") return null;
        s.duel = null;
        s.duelBanner = { winner: d.names[d.winner], loser: d.names[d.loser], ts: Date.now() };
        return s;
      });
    }, 3200);
    return () => { if (cleanupRef.current) { clearTimeout(cleanupRef.current); cleanupRef.current = null; } };
  }, [isHost, duel, publish]);

  const castSkill = useCallback((comp) => {
    publish((s) => {
      const d = s.duel;
      if (!d || d.phase !== "active" || d.turnOrder[d.turnIndex] !== myId) return null;
      const oppId = myId === d.a ? d.b : d.a;
      if ((d.energy?.[myId] ?? 0) < (comp.cout || 0)) return null;
      const meCh = s.players[myId]?.character;
      const opCh = s.players[oppId]?.character;
      if (!meCh || !opCh) return null;
      const res = computeDamageWithFormula(meCh.stats, opCh.stats, {
        formula: comp.formula || ["atk"],
        multiplier: comp.multiplier || 1,
        atkElement: CLASSES[d.classes[myId]]?.element,
        defElement: CLASSES[d.classes[oppId]]?.element,
      });
      const dmg = res.hit ? res.damage : 0;
      d.hp[oppId] = Math.max(0, (d.hp[oppId] ?? 0) - dmg);
      d.energy[myId] = Math.max(0, (d.energy[myId] ?? 0) - (comp.cout || 0));
      d.log = [...(d.log || []), res.hit
        ? `${comp.icon || "⚔️"} ${d.names[myId]} — ${comp.nom} : -${dmg} PV à ${d.names[oppId]}${res.crit ? " (CRIT)" : ""}.`
        : `${d.names[myId]} rate ${comp.nom} !`].slice(-8);
      if (d.hp[oppId] <= 0) {
        d.phase = "done";
        d.winner = myId;
        d.loser = oppId;
        const win = s.players[myId];
        const los = s.players[oppId];
        win.character = { ...win.character, stats: { ...win.character.stats, pv: Math.max(1, d.hp[myId]) } };
        los.character = { ...los.character, stats: { ...los.character.stats, pv: 0 } };
        los.alive = false;
        d.log = [...d.log, `🏆 ${d.names[myId]} remporte le duel !`].slice(-8);
        return s;
      }
      advanceTurn(d);
      return s;
    });
    SFX.hit?.();
  }, [publish, myId]);

  const defend = useCallback(() => {
    publish((s) => {
      const d = s.duel;
      if (!d || d.phase !== "active" || d.turnOrder[d.turnIndex] !== myId) return null;
      d.energy[myId] = Math.min(ENERGY_MAX, (d.energy[myId] ?? 0) + 2);
      d.log = [...(d.log || []), `🛡️ ${d.names[myId]} se prépare (+2 énergie).`].slice(-8);
      advanceTurn(d);
      return s;
    });
    SFX.click?.();
  }, [publish, myId]);

  if (!duel) return null;
  const { a, b } = duel;
  if (myId !== a && myId !== b) return null; // non-participants keep exploring

  const oppId = myId === a ? b : a;
  const activeUid = duel.turnOrder[duel.turnIndex];
  const isMyTurn = duel.phase === "active" && activeUid === myId;
  const myEnergy = duel.energy?.[myId] ?? 0;
  const myClass = CLASSES[duel.classes?.[myId]];
  const oppClass = CLASSES[duel.classes?.[oppId]];
  const competences = (myClass?.competences || []).filter((c) => c.type === "attaque");
  const iWon = duel.phase === "done" && duel.winner === myId;

  const Fighter = ({ uid, cls, side }) => (
    <div data-testid={`duel-fighter-${side}`} className={`bg-slate-900 border-4 rounded-2xl p-3 flex-1 ${activeUid === uid && duel.phase === "active" ? "border-emerald-400" : "border-slate-950"} ${uid === myId ? "ring-2 ring-fuchsia-400" : ""}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${cls?.couleur} border-2 border-slate-950 overflow-hidden`}>
          {cls?.image && <img src={cls.image} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold truncate">{duel.names[uid]}{uid === myId ? " (toi)" : ""}</p>
          <p className="text-[11px] text-slate-400 truncate">{cls?.nom}</p>
        </div>
      </div>
      <HealthBar current={Math.max(0, duel.hp[uid] || 0)} max={duel.maxHp[uid] || 1} testId={`duel-hp-${side}`} />
      <div className="flex items-center gap-1 mt-1 text-[11px] text-sky-300">
        <Zap className="w-3 h-3" /> {duel.energy?.[uid] ?? 0}/{ENERGY_MAX}
      </div>
    </div>
  );

  return (
    <div data-testid="coop-duel" className="absolute inset-0 z-40 bg-slate-950/95 flex flex-col overflow-hidden">
      <div className="px-4 py-2 border-b-4 border-slate-950 bg-slate-800 flex items-center justify-between">
        <span className="font-display font-bold text-sm uppercase tracking-wider text-rose-300 flex items-center gap-1">
          <Swords className="w-4 h-4 text-rose-400" /> Duel — Manche {duel.round || 1}
        </span>
        <span data-testid="duel-turn-indicator" className="text-sm font-bold">
          {duel.phase === "done"
            ? (iWon ? <span className="text-emerald-400">VICTOIRE !</span> : <span className="text-rose-400">DÉFAITE…</span>)
            : isMyTurn ? <span className="text-emerald-400">À TON tour</span>
            : <span className="text-amber-400">Tour de {duel.names[activeUid]}</span>}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-stretch gap-2 sm:gap-4">
            <Fighter uid={myId} cls={myClass} side="me" />
            <div className="flex items-center justify-center text-3xl font-display font-bold text-slate-500">VS</div>
            <Fighter uid={oppId} cls={oppClass} side="opp" />
          </div>

          {duel.phase === "done" && (
            <div data-testid="duel-result" className={`mt-4 text-center font-display font-bold text-xl ${iWon ? "text-emerald-400" : "text-rose-400"}`}>
              {iWon ? (
                <p className="flex items-center justify-center gap-2"><Crown className="w-6 h-6 text-yellow-400" /> Tu as vaincu {duel.names[oppId]} !</p>
              ) : (
                <p>{duel.names[duel.winner]} t'a vaincu. Un allié peut te ranimer (sérum).</p>
              )}
            </div>
          )}

          <div data-testid="duel-log" className="mt-3 bg-slate-900 border-2 border-slate-700 rounded-xl px-3 py-2 max-h-28 overflow-y-auto text-xs">
            {(duel.log || []).slice(-6).map((l, i) => <p key={i} className="text-slate-300">• {l}</p>)}
          </div>
        </div>
      </div>

      <div className="border-t-4 border-slate-950 bg-slate-800 p-3">
        {duel.phase === "active" ? (
          <div className="flex flex-wrap gap-2 justify-center max-w-4xl mx-auto">
            {competences.map((comp) => {
              const canCast = isMyTurn && myEnergy >= (comp.cout || 0);
              return (
                <button key={comp.id} data-testid={`duel-skill-${comp.id}`}
                  onClick={() => canCast ? castSkill(comp) : SFX.miss?.()}
                  disabled={!isMyTurn}
                  title={comp.description}
                  className="bg-yellow-400 text-slate-950 font-display font-bold py-2 px-3 rounded-2xl border-4 border-slate-950 cartoon-shadow disabled:opacity-40 disabled:cursor-not-allowed flex flex-col items-center gap-0.5 min-w-[96px]">
                  <span className="text-lg">{comp.icon}</span>
                  <span className="text-xs leading-tight text-center">{comp.nom}</span>
                  <span className="text-[10px] opacity-80">{comp.cout === 0 ? "Gratuit" : `${comp.cout}⚡`}{comp.ultimate ? " · ULT" : ""}</span>
                </button>
              );
            })}
            <button data-testid="duel-defend-btn" onClick={defend} disabled={!isMyTurn}
              className="bg-blue-600 text-white font-display font-bold py-2 px-3 rounded-2xl border-4 border-slate-950 cartoon-shadow disabled:opacity-40 flex flex-col items-center gap-0.5 min-w-[96px]">
              <Swords className="w-5 h-5" />
              <span className="text-xs">Défendre</span>
              <span className="text-[10px] opacity-80">+2⚡</span>
            </button>
          </div>
        ) : (
          <p className="text-center text-slate-300 text-sm py-2">Duel terminé…</p>
        )}
        {!isMyTurn && duel.phase === "active" && (
          <p className="text-center text-amber-300 text-xs mt-2">En attente du tour de {duel.names[activeUid]}…</p>
        )}
      </div>
    </div>
  );
};

export default CoopDuel;
